import React, { useEffect, useState } from 'react';
import Input from '../components/common/Input';
import Switch from '../components/common/Switch';
import RadioButton from '../components/common/RadioButton';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  useGetUserSubscriptionQuery,
  useRestartUserSubscriptionMutation,
} from '../redux/apiSlice';
import { subscriptionOptions } from '../components/Signup/data';
import { Loader, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import UpdatePaymentMethod from '../components/Payment/UpdatePaymentMethod';

const ReactivateSubscriptionModal = ({
  setIsModalOpen,
  onConfirm,
  isLoading,
}: {
  setIsModalOpen: (isOpen: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
}) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="flex flex-col items-center justify-center bg-white shadow-lg rounded-lg w-full max-w-md p-8 border border-gray-200">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-4">
            Reactivate Subscription
          </h3>
          <p className="mb-6">
            Are you sure you want to reactivate your subscription?
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              onClick={() => setIsModalOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-[#023f81] text-white rounded hover:bg-blue-700 min-w-[100px]"
              onClick={onConfirm}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="animate-spin h-5 w-5 m-auto" />
              ) : (
                'Yes, Reactivate'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SubscriptionAndPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { createdAt, expiredAt } = location.state || {};
  const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
  const [isUpdatePaymentModalOpen, setIsUpdatePaymentModalOpen] =
    useState(false);

  const {
    data: userSubscription,
    error,
    isLoading,
    refetch,
  } = useGetUserSubscriptionQuery();

  useEffect(() => {
    refetch();
  }, []);

  const [restartUserSubscription, { isLoading: isReactivating }] =
    useRestartUserSubscriptionMutation();
  const userDetails = useSelector((state: any) => state.app.user);

  console.log('userDetails', userDetails);
  const handleNoOp = () => {
    console.log('test input');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader className="animate-spin" size={48} />
      </div>
    );
  }

  if (error) return <p>Error loading profile data</p>;

  const getSubscriptionLabel = (planId: string | undefined) => {
    getSubscriptionLabel;
    const plan = subscriptionOptions.find((option) => option.id === planId);
    return plan ? plan.label : 'Unknown Plan';
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error fetching subscription details.</div>;
  }

  const handleReactivateSubscription = async () => {
    try {
      await restartUserSubscription({
        user_id: userDetails?.user,
      }).unwrap();

      toast.success('Subscription reactivated successfully!');
      setIsReactivateModalOpen(false);
      refetch(); // Refetch subscription data after successful reactivation
    } catch (error) {
      toast.error('Failed to reactivate subscription. Please try again.');
    }
  };

  return (
    <div className="flex flex-col text-[14px] font-roboto md:flex-row justify-between p-8 gap-5 bg-gray-50 min-h-screen">
      {/* Profile Section */}
      <div className="bg-white shadow-md rounded-lg p-6 w-full md:w-1/2 mb-6 md:mb-0">
        <h2 className="text-[28px] font-bold mb-4">Subscription Management</h2>
        <div className="mb-4">
          <label className="block text-black mb-2">
            Current Subscription Start Date
          </label>
          <Input
            type="text"
            value={userSubscription?.current_plan_starting_date || createdAt} // Assuming subscription object has startDate
            onChange={handleNoOp}
            readOnly
            className="rounded-lg border-[#505050]"
            disabled={false}
          />
        </div>
        <div className="mb-4">
          <label className="block text-black mb-2">
            Current Subscription End Date
          </label>
          <Input
            type="text"
            value={userSubscription?.current_plan_ending_date || expiredAt} // Assuming subscription object has endDate
            onChange={handleNoOp}
            readOnly
            className="rounded-lg border-[#505050]"
            disabled={false}
          />
        </div>
        <div className="mb-4">
          <label className="block text-black mb-2">Subscription Type</label>
          <Input
            type="text"
            value={userSubscription?.plan} // Get the label for the plan
            onChange={handleNoOp}
            readOnly
            className="rounded-lg border-[#505050]"
            disabled={false}
          />
        </div>
        <div className="mb-4">
          {userSubscription?.is_plan_cancelled ? (
            <button
              className="bg-[#023f81] text-white px-4 py-2 rounded mt-4 ml-4"
              onClick={() => handleReactivateSubscription()}
            >
              {isReactivating ? (
                <Loader2 className="animate-spin h-5 w-5 m-auto" />
              ) : (
                'Reactivate Subscription'
              )}
            </button>
          ) : (
            <Link to={'/profile/cancel-subscription'}>
              <button className="bg-[#023f81] text-white px-4 py-2 rounded mt-4">
                Cancel Subscription
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* Account Settings Section */}
      <div className="bg-white shadow-md rounded-lg p-6 w-full md:w-1/2">
        <h2 className="text-[28px] font-bold mb-4">Payment Information</h2>

        {/* Email Update Section */}
        {isUpdatePaymentModalOpen ? (
          <UpdatePaymentMethod
            setIsUpdatePaymentModalOpen={setIsUpdatePaymentModalOpen}
          />
        ) : (
          <div className="mb-4">
            <button
              onClick={() => setIsUpdatePaymentModalOpen(true)}
              className="bg-[#023f81] text-white px-4 py-2 rounded mt-4"
            >
              Update Payment Info
            </button>
          </div>
        )}
      </div>

      {isReactivateModalOpen && (
        <ReactivateSubscriptionModal
          setIsModalOpen={setIsReactivateModalOpen}
          onConfirm={handleReactivateSubscription}
          isLoading={isReactivating}
        />
      )}
    </div>
  );
};

export default SubscriptionAndPayment;
